import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useLazyGetUserScopeExclusionsPageQuery,
  useLazyGetUserScopesPageQuery,
} from '../services';
import type { UserScopeExclusionRecord, UserScopeRecord } from '../models';
import {
  loadAllUserScopeExclusions,
  loadAllUserScopes,
} from '../utils/fetchUserScopeLists';

type UserScopeListsState = {
  scopes: UserScopeRecord[];
  exclusions: UserScopeExclusionRecord[];
  isLoading: boolean;
  isError: boolean;
};

const emptyState = (): UserScopeListsState => ({
  scopes: [],
  exclusions: [],
  isLoading: true,
  isError: false,
});

/**
 * Carga todas las páginas de scopes y exclusiones del usuario (GET paginados del backend).
 */
const useUserScopeLists = (userId: string | undefined) => {
  const [fetchScopesPage] = useLazyGetUserScopesPageQuery();
  const [fetchExclusionsPage] = useLazyGetUserScopeExclusionsPageQuery();

  const fetchScopesRef = useRef(fetchScopesPage);
  const fetchExclusionsRef = useRef(fetchExclusionsPage);
  fetchScopesRef.current = fetchScopesPage;
  fetchExclusionsRef.current = fetchExclusionsPage;

  const [state, setState] = useState<UserScopeListsState>(emptyState);

  const reload = useCallback(async () => {
    if (!userId) {
      setState({
        scopes: [],
        exclusions: [],
        isLoading: false,
        isError: false,
      });
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: true,
      isError: false,
    }));

    try {
      const [scopes, exclusions] = await Promise.all([
        loadAllUserScopes(
          (args) => fetchScopesRef.current(args),
          userId
        ),
        loadAllUserScopeExclusions(
          (args) => fetchExclusionsRef.current(args),
          userId
        ),
      ]);

      setState({
        scopes,
        exclusions,
        isLoading: false,
        isError: false,
      });
    } catch {
      setState({
        scopes: [],
        exclusions: [],
        isLoading: false,
        isError: true,
      });
    }
  }, [userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    scopes: state.scopes,
    exclusions: state.exclusions,
    isLoading: state.isLoading,
    isError: state.isError,
    reload,
  };
};

export default useUserScopeLists;
